import React from "react";
import { Link } from "react-router-dom";
import { Sun, Droplet, Snowflake, PawPrint, ArrowRight } from "lucide-react";
import { CARE_GUIDES } from "../../data/careGuides";
import { PLANTS } from "../../data/plants";
import { SITE_IMAGES } from "../../data/siteImages";
import PlantImage from "../../components/fancyplants/PlantImage";
import Seo from "../../components/fancyplants/Seo";

export default function CareLibraryPage() {
  return (
    <div className="pt-24">
      <Seo
        title="Care Library"
        description="Honest, Ireland-specific plant care — watering, light, toxicity and how each plant copes with damp, low-light winters."
        path="/care"
      />
      <header className="border-b border-line bg-forest-900 text-canvas">
        <div className="container-fp grid items-center gap-10 py-16 lg:grid-cols-[1fr_420px]">
          <div>
            <p className="eyebrow mb-3">The Care Library</p>
            <h1 className="t-h1 max-w-3xl text-canvas">
            Plant care, written for the Irish weather.
            </h1>
            <p className="t-lead mt-4 max-w-2xl text-canvas/75">
            Honest, specific answers to the questions plant owners actually ask — toxicity,
            watering, light, and how each plant copes with our damp, low-light winters.
            </p>
          </div>
          <PlantImage
            src={SITE_IMAGES.careLibraryHero}
            alt="Young plant seedlings in propagation trays"
            ratio="4/3"
            className="rounded-3xl"
            priority
          />
        </div>
      </header>

      <div className="container-fp py-14">
        <div className="grid gap-6 md:grid-cols-2">
          {CARE_GUIDES.map((g) => {
            const plant = PLANTS.find((p) => p.careGuideId === g.id);
            return (
              <Link key={g.id} to={`/care/${g.slug}`} className="group surface overflow-hidden hover:shadow-lift">
                <PlantImage
                  src={plant?.images[0] || SITE_IMAGES.plantShelf}
                  alt={plant ? `${plant.name} foliage` : `${g.plantName} foliage`}
                  ratio="3/2"
                />
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="font-display text-2xl leading-tight group-hover:text-forest-700">
                        {g.plantName}
                      </h2>
                      <p className="mt-2 text-ink-soft">{g.summary}</p>
                    </div>
                    <ArrowRight size={20} className="mt-1 shrink-0 text-brass transition-transform group-hover:translate-x-1" />
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Stat icon={Sun} text={g.stats.light} />
                    <Stat icon={Droplet} text={g.stats.water} />
                    <Stat icon={Snowflake} text={g.stats.hardiness} />
                    <Stat icon={PawPrint} text={g.stats.toxicity} />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, text }) {
  return (
    <span className="chip">
      <Icon size={13} className="text-forest-500" /> {text}
    </span>
  );
}
